import {MDIcon, MDIconSize} from '@mintdata-wsp/common/components/icon/MDIcon';
import {MDIcons} from '@mintdata-wsp/common/components/icon/MDIcons';
import {rootState} from '@mintdata-wsp/common/state/RootState';
import {Page, PageId} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/Page';
import {SurfaceState} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/SurfaceState';
import {controllers} from '@mintdata-wsp/wsp/controllers/WspControllers';
import {ErrorPopupSide, ErrorStyle, Input, InputStyle} from '@mintdata-wsp/wsp/ui/components/forms/Input';
import {Scrollable, ScrollBehavior} from '@mintdata-wsp/wsp/ui/components/scrollable/Scrollable';
import {DeleteDialog} from '@mintdata-wsp/wsp/ui/dialogs/DeleteDialog';
import {ActiveUserDots} from '@mintdata-wsp/wsp/ui/views/visualDesigner/common/ActiveUserDots';
import {PagesOutlineContextMenu} from '@mintdata-wsp/wsp/ui/views/visualDesigner/surface/panels/outline/pages/contextMenu/PagesOutlineContextMenu';
import {SectionOutlineContextMenu} from '@mintdata-wsp/wsp/ui/views/visualDesigner/surface/panels/outline/pages/contextMenu/SectionOutlineContextMenu';
import {OverlaysMenu} from '@mintdata-wsp/wsp/ui/views/visualDesigner/surface/panels/outline/pages/overlaysMenu/OverlaysMenu';
import {PagesDropdown} from '@mintdata-wsp/wsp/ui/views/visualDesigner/surface/panels/outline/pages/PagesDropdown';
import {SurfaceOutlinePanelPartWrapper} from '@mintdata-wsp/wsp/ui/views/visualDesigner/surface/panels/outline/SurfaceOutlinePanelPartWrapper';
import {Point} from '@mintdata/mint-kit/api/models/Point';
import {Window} from '@mintdata/mint-kit/impl/components/popover/Window';
import {ArrayUtils} from '@mintdata/mint-kit/impl/util/ArrayUtils';
import bind from 'bind-decorator';
import * as classnames from 'classnames/bind';
import {action, observable} from 'mobx';
import {observer} from 'mobx-react';
import * as React from 'react';
import {DraggableCore, DraggableData} from 'react-draggable';

const styles = require('./SurfacePagesOutline.less');
const cx = classnames.bind(styles);

enum PageDropPlace
{
    Above = 'Above',
    Below = 'Below',
    Into = 'Into'
}

interface DragOverPageCtx
{
    pageId:PageId;
    pageDropPlace:PageDropPlace;
}

interface IRenamingPageCtx
{
    pageId:PageId;
    newValue:string;
}

interface ISurfacePagesOutlineState
{
    isDragging:boolean;
    openedContextMenuPageId:PageId;
    selectedPages:PageId[],
    dragOverPageCtx:DragOverPageCtx;
    dropPlace:PageDropPlace;
    draggingPagesPosition:Map<PageId, Point>;
    itemRefs:Map<PageId, HTMLElement>;
    renamingPageCtx:IRenamingPageCtx;
}

interface SurfacePagesOutlineProps
{
    onExpand?:() => void,
    onCollapse?:() => void,
    className?:string,
}

@observer
export class SurfacePagesOutline extends React.Component<SurfacePagesOutlineProps, ISurfacePagesOutlineState>
{
    @observable
    private _state:ISurfacePagesOutlineState = {
        isDragging: false,
        openedContextMenuPageId: null,
        selectedPages: [],
        dragOverPageCtx: {
            pageId: null,
            pageDropPlace: null
        },
        dropPlace: null,
        draggingPagesPosition: new Map(),
        itemRefs: new Map(),
        renamingPageCtx: {
            pageId: null,
            newValue: null
        }
    };

    private static START_DRAGGING_AFTER_DELTA = 4;

    private _refHandlers = {
        forItem: (pageId:PageId):(ref:HTMLDivElement | null) => void => (
            (ref:HTMLDivElement | null) =>
            {
                if( ref )
                    this._state.itemRefs.set(pageId, ref);
                else
                    this._state.itemRefs.delete(pageId);
            }
        )
    };

    private _lastActiveElement:HTMLElement = null;

    public componentDidMount()
    {
        document.addEventListener('mousedown', this._handleDocumentClick, false);
    }

    public componentWillUnmount()
    {
        document.removeEventListener('mousedown', this._handleDocumentClick, false);
    }

    @action
    private _handleDocumentClick = (event:MouseEvent) =>
    {
        const {renamingPageCtx, itemRefs} = this._state;

        if( renamingPageCtx.pageId )
        {
            const renamingBoxRef = itemRefs.get(renamingPageCtx.pageId);

            if(
                renamingBoxRef &&
                renamingBoxRef !== event.target &&
                renamingBoxRef.contains(event.target as any)
            )
                return;

            // handle click that happened outside the renaming box
            this._renamePage(renamingPageCtx.pageId);
        }
    };

    render()
    {
        const {surfacePagesPanel, workbenchVisibility} = controllers.ui;
        const {className} = this.props;
        const showRemoveSectionConfirmDialog:boolean = surfacePagesPanel.shouldShowRemoveSectionConfirmDialog();
        const isCollapsed:boolean = workbenchVisibility.isSurfacePagesOutlinePanelCollapsed();
        const isCopyPagesOutlineShown:boolean = workbenchVisibility.isSurfaceCopyPagesPanelShown();

        return (
            <SurfaceOutlinePanelPartWrapper
                className={cx('base', {[className]: !!className})}
                title={this._getPagesOutlineHeader()}
                isCollapsed={isCollapsed}
                onCollapseChanged={this._setSurfacePagesOutlinePanelCollapsed}
                leftActionItems={[{
                    tooltipText: 'Import pages/sheets from another design',
                    icon: MDIcons.common.PlaylistPlus,
                    handler: this._handleCopyPagesClick,
                    className: cx('copyPagesToggler', {copyPagesToggler__active: isCopyPagesOutlineShown})
                }]}
                rightActionItems={[{
                    tooltipText: 'Add Page',
                    icon: MDIcons.common.Add,
                    handler: this._handleAddPageClick
                }]}
            >
                <div className={cx('scrollableWrapper')}>
                    <Scrollable
                        isScrollX={true}
                        isScrollY={true}
                        className={cx('itemsList')}
                        id={'surfacePagesOutline_itemsList'}
                        behavior={ScrollBehavior.OnHover}
                    >
                        {this._renderPagesList()}
                        {showRemoveSectionConfirmDialog && this._renderRemoveSectionConfirmDialog()}
                    </Scrollable>
                </div>
            </SurfaceOutlinePanelPartWrapper>
        );
    }

    private _renderPagesList():JSX.Element[] | null
    {
        const {surfaceArea} = controllers.ui;

        const surfaceState:SurfaceState = surfaceArea.getState();

        if( !surfaceState )
            return null;

        const sectionPages = ArrayUtils.groupArray(surfaceState.pagesItemsOrdered, page => page.sectionName || '');
        const sortedSections = Object.keys(sectionPages).sort((key1, key2) =>
        {
            if( key1 === '' ) return -1;
            if( key2 === '' ) return 1;
            return key1.localeCompare(key2);
        });

        return sortedSections.map(sectionName =>
            this._renderSection(sectionName, sectionPages[sectionName] as Array<Page>)
        );
    }

    private _renderSection(sectionName:string, sectionPages:Array<Page>):JSX.Element
    {
        return (
            <div key={sectionName}>
                {sectionName && <SectionOutlineContextMenu
                    sectionName={sectionName}
                >
                    <div className={cx('sectionName')}>{sectionName}</div>
                </SectionOutlineContextMenu>
                }
                {
                    sectionPages.map((page:Page) => this._renderPage(page))
                }
            </div>
        );
    }

    private _renderPage(page:Page):JSX.Element
    {
        const {surfaceArea} = controllers.ui;
        const surfaceState = surfaceArea.getState();

        const {renamingPageCtx, openedContextMenuPageId} = this._state;

        const activePage:Page = surfaceArea.getActivePage();
        const isSelected:boolean = activePage && activePage.id === page.id;
        const isOpenedContextMenuPage:boolean = page.id === openedContextMenuPageId;
        const itemName:JSX.Element | string = renamingPageCtx.pageId === page.id
            ? this._renderRenameEditor(page)
            : page.name;

        return (
            <PagesOutlineContextMenu
                key={page.id}
                pageId={page.id}
                onShow={this._handleCtxMenuShow(page.id)}
                onHide={this._handleCtxMenuHide}
            >
                <div className={cx('itemWrapper')}>
                    <DraggableCore
                        onStart={this._getItemDragStartHandler}
                        onDrag={this._getItemDragHandler(page)}
                        onStop={this._getItemDragStopHandler}
                        onMouseDown={this._handleDraggableMouseDown(page)}
                        enableUserSelectHack={false}
                    >
                        <div
                            ref={this._refHandlers.forItem(page.id)}
                            data-page-id={page.id}
                            className={cx('item', {isSelected, isOpenedContextMenuPage})}
                            onClick={this._handleItemClick(page)}
                            onDoubleClick={this._getItemDoubleClickHandler(page)}
                            data-component={isSelected ? 'selected-page' : 'page'}
                        >
                            {
                                surfaceState.startPageId === page.id &&
                                <MDIcon
                                    className={cx('homeIcon')}
                                    name={MDIcons.common.Home}
                                    size={MDIconSize.medium20}
                                />
                            }
                            <span className={cx('name')}>{itemName}</span>
                            <ActiveUserDots filterPageId={page.id} />
                            {isSelected && this._renderOverlaysMenu(page)}
                            {this._renderDropTopPlaceholder(page)}
                            {this._renderDropBottomPlaceholder(page)}
                        </div>
                    </DraggableCore>

                    {this._renderDraggingElement(page)}
                </div>
            </PagesOutlineContextMenu>
        );
    }

    private _getPagesOutlineHeader():string | JSX.Element
    {
        const {workbenchVisibility, surfaceArea} = controllers.ui;
        const isCollapsed:boolean = workbenchVisibility.isSurfacePagesOutlinePanelCollapsed();

        if( !isCollapsed )
            return 'Pages';

        const surfaceState:SurfaceState = surfaceArea.getState();
        const activePage:Page = surfaceState && surfaceState.activePage;

        if( !activePage )
            return null;

        return <PagesDropdown />;
    }

    private _onDismissed = () =>
    {
        const {surfacePagesPanel, surfaceArea} = controllers.ui;

        surfacePagesPanel.hideRemoveSectionConfirmDialog();
        this.focusLastActiveElement();
    };

    private _renderRemoveSectionConfirmDialog():JSX.Element
    {
        const {surfacePagesPanel, surfaceArea} = controllers.ui;
        const removeSectionName = surfacePagesPanel.getRemoveSectionName();

        return (
            <Window
                id='DeleteDialog'
                onDismissed={this._onDismissed}
                hasModalBackground={true}
            >
                <DeleteDialog
                    message="Are you sure you want to delete this section with all it's pages?"
                    onClose={this._onDismissed}
                    onClickDelete={() =>
                    {
                        surfaceArea.removeAllPagesFromSection(removeSectionName);
                        surfacePagesPanel.hideRemoveSectionConfirmDialog();
                        this.focusLastActiveElement();
                    }}
                />
            </Window>
        );
    }

    @bind
    public focusLastActiveElement():void
    {
        if( this._lastActiveElement )
            this._lastActiveElement.focus();
    }

    @bind
    private _setSurfacePagesOutlinePanelCollapsed(isCollapsed:boolean):void
    {
        const {onCollapse, onExpand} = this.props;
        controllers.ui.workbenchVisibility.setSurfacePagesOutlinePanelCollapsed(isCollapsed);

        if( isCollapsed && onCollapse )
            onCollapse();
        else if( onExpand )
            onExpand();
    }

    @action
    private _handleCtxMenuShow = (pageId:PageId):() => void =>
        action(() =>
        {
            this._state.openedContextMenuPageId = pageId;
            this._lastActiveElement = document.activeElement as HTMLElement;
        });

    @action
    private _handleCtxMenuHide = ():void =>
    {
        this._state.openedContextMenuPageId = null;
        this.focusLastActiveElement();
    };

    private _handleAddPageClick = ():void =>
    {
        const {surfaceArea, workbenchVisibility} = controllers.ui;
        surfaceArea.addNewPage();

        if( workbenchVisibility.isSurfacePagesOutlinePanelCollapsed() )
        {
            workbenchVisibility.setSurfacePagesOutlinePanelCollapsed(false);
            this.props.onExpand && this.props.onExpand();
        }
    };

    private _handleCopyPagesClick = ():void =>
    {
        const {workbenchVisibility} = controllers.ui;
        workbenchVisibility.toggleCopyPagesPanelShown();
    };

    private _handleItemClick = (page:Page):(event:React.SyntheticEvent<any>) => void =>
    {
        return (event:React.SyntheticEvent<any>) =>
        {
            event.stopPropagation();
            const {surfaceArea} = controllers.ui;

            surfaceArea.setActivePage(page);
        };
    };

    private _getItemDoubleClickHandler = (page:Page):(event:any) => void => (
        action((event:any) =>
        {
            event.stopPropagation();

            this._state.renamingPageCtx.pageId = page.id;
        })
    );

    private _renderOverlaysMenu(page:Page):JSX.Element
    {
        if( page.overlays.length === 0 )
            return null;

        return <OverlaysMenu className={cx('overlaysMenu')} page={page} />;
    }

    private _renderRenameEditor(page:Page):JSX.Element
    {
        const {renamingPageCtx} = this._state;
        const {surfaceArea} = controllers.ui;

        return (
            <Input
                className={cx('renameEditor')}
                inputClassName={cx('renameEditorInput')}
                style={InputStyle.Simple}
                initialValue={page.name}
                isInitiallySelected={true}
                isInitiallyFocused={true}
                isValidFn={(newValue:string) => surfaceArea.isPageNameUnique(page.id, newValue)}
                errorStyle={ErrorStyle.PropPanel}
                errorPopupSide={ErrorPopupSide.Bottom}
                getErrorMessageFn={() => 'Page name should be unique'}
                onChange={(newValue:string) => renamingPageCtx.newValue = newValue}
                onEnterKey={() => this._renamePage(page.id)}
            />
        );
    }

    @action
    private _handleNameInputChange = (event:React.FormEvent<any>):void =>
    {
        const userInput:string = (event.target as any).value;

        this._state.renamingPageCtx.newValue = userInput;
    };

    @action
    private _renamePage(pageId:PageId)
    {
        const {surfaceArea} = controllers.ui;
        const {renamingPageCtx} = this._state;
        let shouldCloseRenameEditor = true;

        if( renamingPageCtx.newValue )
        {
            shouldCloseRenameEditor = surfaceArea.renamePage(pageId, renamingPageCtx.newValue);
        }

        shouldCloseRenameEditor && this._closeRenameEditor();
    }

    @action
    private _closeRenameEditor()
    {
        const {renamingPageCtx} = this._state;

        renamingPageCtx.pageId = null;
        renamingPageCtx.newValue = null;
    }

    private _handleDraggableMouseDown = (page:Page):(event:MouseEvent) => void => (
        action((event:MouseEvent):void =>
        {
            this._state.selectedPages = [page.id];

            if( event.shiftKey )
            {
                // select multiple pages
            }
        })
    );

    @action.bound
    private _getItemDragStartHandler(event:MouseEvent, data:DraggableData):void
    {
        this._state.selectedPages.forEach((pageId:PageId) =>
        {
            const pageElement:HTMLElement | null = this._state.itemRefs.get(pageId);
            const pageClientRect = pageElement.getBoundingClientRect();

            this._state.draggingPagesPosition.set(pageId, Point.from(pageClientRect.left, pageClientRect.top));
        });
    }

    private _getItemDragHandler = (page:Page):(event:MouseEvent, data:DraggableData) => void => (
        action((event:MouseEvent, data:DraggableData):void =>
        {
            const {draggingPagesPosition, isDragging} = this._state;

            // drag has been canceled
            if( !draggingPagesPosition.size )
                return;

            if( this._isStartedDragAllowed(page) )
                this._state.isDragging = true;

            const {x, y} = draggingPagesPosition.get(page.id);
            draggingPagesPosition.set(page.id, Point.from(x + data.deltaX, y + data.deltaY));
        })
    );

    @action.bound
    private _getItemDragStopHandler(event:MouseEvent, data:DraggableData):void
    {
        const {surfaceArea} = controllers.ui;
        const {dragOverPageCtx, selectedPages} = this._state;

        if( !selectedPages.length || !dragOverPageCtx.pageId )
        {
            this._stopDrag();
            return;
        }

        if( dragOverPageCtx.pageDropPlace === PageDropPlace.Above )
        {
            const afterPageId:PageId = dragOverPageCtx.pageId;

            selectedPages.forEach((selectedPageId:PageId) => surfaceArea.moveBeforePage(selectedPageId, afterPageId));
        }
        else if( dragOverPageCtx.pageDropPlace === PageDropPlace.Below )
        {
            let beforePageId:PageId = dragOverPageCtx.pageId;

            selectedPages.forEach((selectedPageId:PageId) =>
            {
                surfaceArea.moveAfterPage(selectedPageId, beforePageId);
                beforePageId = selectedPageId;
            });
        }

        surfaceArea.setActivePage(surfaceArea.getState().pages.get(selectedPages[0]));
        this._stopDrag();
    }

    private _renderDraggingElement(page:Page):JSX.Element
    {
        const {isDragging, draggingPagesPosition, selectedPages} = this._state;

        if( isDragging && selectedPages.indexOf(page.id) > -1 )
        {
            const itemPaddingStyle = SurfacePagesOutline._calculateItemPadding();

            return (
                <div
                    className={cx('draggingElement')}
                    style={{
                        ...itemPaddingStyle,
                        left: draggingPagesPosition.get(page.id).x,
                        top: draggingPagesPosition.get(page.id).y
                    }}
                >
                    {page.name}
                </div>
            );
        }

        return null;
    }

    // only for the first item in a subtree
    private _renderDropTopPlaceholder(page:Page):JSX.Element
    {
        const {isDragging, dragOverPageCtx} = this._state;

        if( isDragging )
        {
            const dropLineOffsetStyle = this._calculateDropLineOffset();

            return (
                <div
                    className={cx('dropTopPlaceholder')}
                    onMouseOver={this._getPlaceholderMouseOverHandler(page.id, PageDropPlace.Above)}
                    onMouseLeave={this._getPlaceholderMouseLeaveHandler(page.id, PageDropPlace.Above)}
                >
                    {
                        dragOverPageCtx.pageId === page.id &&
                        dragOverPageCtx.pageDropPlace === PageDropPlace.Above &&
                        <div
                            className={cx('dropLine')}
                            style={{...dropLineOffsetStyle}}
                        />
                    }
                </div>
            );
        }

        return null;
    }

    private _renderDropBottomPlaceholder(page:Page):JSX.Element
    {
        const {dragOverPageCtx, isDragging} = this._state;

        if( isDragging )
        {
            const dropLineOffsetStyle = this._calculateDropLineOffset();
            const placeHolderStyle = {left: 0};
            const dropLineStyle = {...dropLineOffsetStyle};

            return (
                <div
                    key={`${page.id}-placeholder`}
                    className={cx('dropBottomPlaceholder')}
                    onMouseOver={this._getPlaceholderMouseOverHandler(page.id, PageDropPlace.Below)}
                    onMouseLeave={this._getPlaceholderMouseLeaveHandler(page.id, PageDropPlace.Below)}
                    style={placeHolderStyle}
                >
                    {
                        dragOverPageCtx.pageId === page.id &&
                        dragOverPageCtx.pageDropPlace === PageDropPlace.Below &&
                        <div
                            className={cx('dropLine')}
                            style={dropLineStyle}
                        />
                    }
                </div>
            );
        }

        return null;
    }

    private _calculateDropLineOffset():React.CSSProperties
    {
        return {
            left: 24,
            right: 8
        };
    }

    private _getPlaceholderMouseOverHandler = (pageId:PageId,
                                               pageDropPlace:PageDropPlace) =>
    {
        return action((event:React.MouseEvent<HTMLDivElement>) =>
        {
            if( this._state.isDragging )
                this._state.dragOverPageCtx = {pageId, pageDropPlace};
        });
    };

    private _getPlaceholderMouseLeaveHandler = (pageId:PageId,
                                                pageDropPlace:PageDropPlace) =>
    {
        return action((event:React.MouseEvent<HTMLDivElement>) =>
        {
            if( this._state.isDragging )
            {
                this._state.dragOverPageCtx = {
                    pageId: null,
                    pageDropPlace: null
                };
            }
        });
    };

    @action
    private _stopDrag()
    {
        this._state.isDragging = false;

        this._state.dragOverPageCtx = {
            pageId: null,
            pageDropPlace: null
        };

        this._state.selectedPages = [];

        this._state.draggingPagesPosition.clear();
    }

    private _isStartedDragAllowed(page:Page):boolean
    {
        const boxElement = this._state.itemRefs.get(page.id);
        const boxClientRect = boxElement.getBoundingClientRect();

        const {x, y} = this._state.draggingPagesPosition.get(page.id);

        return (
            Math.abs(x - boxClientRect.left) > SurfacePagesOutline.START_DRAGGING_AFTER_DELTA ||
            Math.abs(y - boxClientRect.top) > SurfacePagesOutline.START_DRAGGING_AFTER_DELTA
        );
    }

    private static _calculateItemPadding():React.CSSProperties
    {
        return {paddingLeft: 32};
    }
}
